import { useAuth } from '@interhyp/util-authentication';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSessionData } from '../api';
import { killSession } from '../redux/StateSession/actions';
import { isAuthenticated, isLoading } from '../redux/StateSession/selectors';
import { sessionConstants } from '../redux/constants';

const FOUR_MINUTES_IN_MS = 240_000;

const useSessionHeartbeat = () => {
  const dispatch = useDispatch();
  const { client, done } = useAuth();

  // kill current session
  const onInvalidSession = useCallback(
    () => dispatch(killSession(sessionConstants.LOGOUT_REASON.TIMEOUT)),
    [dispatch],
  );

  const authenticated = useSelector(isAuthenticated);
  const loading = useSelector(isLoading);
  const sessionHeartbeatTimeout = useRef(null);
  const visibilityChangeTimeout = useRef(null);
  const lastActivityTimestamp = useRef(new Date());
  const hasRequestPending = useRef(false);

  const updateSessionHeartbeat = useCallback(async () => {
    if (sessionHeartbeatTimeout.current) {
      clearTimeout(sessionHeartbeatTimeout.current);
      sessionHeartbeatTimeout.current = null;
    }
    // send heartbeat only if session is active and session not loading (after reload)
    if (authenticated && !loading && !hasRequestPending.current) {
      hasRequestPending.current = true;
      const sessionData = await getSessionData();
      hasRequestPending.current = false;

      // check for invalid session
      if (!sessionData) {
        onInvalidSession();
      } else {
        // send next heartbeat delayed
        sessionHeartbeatTimeout.current = setTimeout(updateSessionHeartbeat, FOUR_MINUTES_IN_MS);
      }
    }
  }, [authenticated, loading, onInvalidSession]);

  useEffect(() => {
    if (client === 'broker' || done === false) {
      // in case of broker, let util-authentication do the keep alive
      return;
    }

    updateSessionHeartbeat();

    if (authenticated) {
      lastActivityTimestamp.current = new Date();

      const visibilityChangeCB = () => {
        // we need to yield this callback to put it at the end of the event loop to prevent sending heartbeat during reload
        visibilityChangeTimeout.current = setTimeout(() => {
          updateSessionHeartbeat();
        }, 0);
      };
      window.addEventListener('visibilitychange', visibilityChangeCB);

      // cleanup - remove all listeners from document
      return () => {
        window.removeEventListener('visibilitychange', visibilityChangeCB);
      };
    }
  }, [authenticated, client, done, lastActivityTimestamp, updateSessionHeartbeat]);
};

export { useSessionHeartbeat };
